import * as Yup from 'yup';
import { FormRegExp } from '../../constants/FormRegExp';
import { AuctionStatusType } from '../../constants/AuctionStatusType';
import { isBeforeOrPresentDate } from '../../common/utils/date/Date.utils';
import { bodyTypes } from '../../constants/bodyTypes';
import { carColours } from '../../constants/carColours';
import { fuelTypes } from '../../constants/fuelTypes';
import { transmissionTypes } from '../../constants/transmissionTypes';
import { VehicleTypes } from '../../constants/VehicleTypes';

export default () =>
  Yup.object().shape({
    numberPlate: Yup.string()
      .required('Field is required')
      .min(1, 'Registration plate must be between 1 and 10 characters')
      .max(10, 'Registration plate must be between 1 and 10 characters'),
    year: Yup.string()
      .required('Field is required')
      .matches(FormRegExp.YEAR, 'Type correct date format')
      .test(
        'valid-year-range',
        'The year cannot be less than 100 years back and not higher than the current one',
        (value) => {
          const currentYear = new Date().getFullYear();
          if (!value) return false;
          const year = parseInt(value, 10);
          return year >= currentYear - 100 && year <= currentYear;
        },
      ),
    mileage: Yup.number()
      .required('Field is required')
      .min(0, 'Mileage must be greater or equal 0')
      .max(999999999, 'Mileage must be smaller or equal 999 999 999'),
    derivative: Yup.string()
      .required('Field is required')
      .min(1, 'Derivative must be between 1 and 150 characters')
      .max(150, 'Derivative must be between 1 and 150 characters'),
    carType: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test('valid-body-type', `Value must be one of: ${bodyTypes.join(', ')}`, (value) => {
        if (!value) return false;
        return bodyTypes.includes(value.value);
      })
      .required('Field is required'),
    transmission: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test(
        'valid-transmission',
        `Possible values are: ${transmissionTypes.join(', ')}`,
        (value) => {
          if (!value) return false;
          return transmissionTypes.includes(value.value);
        },
      )
      .required('Field is required'),
    engineSize: Yup.number()
      .when('fuel', {
        is: (value) => value !== 'Electric',
        then: () =>
          Yup.number()
            .required('Field is required')
            .min(1, 'Engine size must be greater than 0')
            .max(999999999, 'Engine size must be smaller or equal 999 999 999'),
      })
      .nullable(),
    fuel: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test('valid-fuel', `Possible values are: ${fuelTypes.join(', ')}`, (value) => {
        if (!value) return false;
        return fuelTypes.includes(value.value);
      })
      .required('Field is required'),
    colour: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test('valid-colour', `Possible values are: ${carColours.join(', ')}`, (value) => {
        if (!value) return false;
        return carColours.includes(value.value);
      })
      .required('Field is required'),
    doors: Yup.string()
      .required('Field is required')
      .matches(FormRegExp.INTEGER, 'Type integer number')
      .test('valid-value', 'Value must be between 1 and 5 included', (value) => {
        if (!value) return true;
        const number = Number(value);
        return number >= 1 && number <= 5;
      }),
    previousOwners: Yup.number()
      .required('Field is required')
      .min(0, 'Number of previous owners must be greater or equal to 0')
      .max(100, 'Number of previous owners must be smaller or equal to 100'),
    numbersOfKeyIncluded: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test('valid-numbers-of-key-included', `Value must be one of: 1, 2, 2+`, (value) => {
        if (!value || !Object.keys(value).length) return true;
        return ['ONE', 'TWO', 'TWO_PLUS'].includes(value.value);
      }),
    registrationDate: Yup.string().required('Field is required'),
    launchDate: Yup.string().required('Field is required'),
    organizationLocations: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test(
        'not-empty-location',
        `Field is required`,
        (value) => value && Object.keys(value).length,
      ),
    vin: Yup.string().required('Field is required').max(17, 'Max length equals 17 characters'),
    services: Yup.array().of(
      Yup.object().shape({
        date: Yup.string().required('Field is required'),
        mileage: Yup.number().required('Field is required'),
        serviceName: Yup.string().required('Field is required'),
      }),
    ),
    type: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    }),
    tryingToDealDeliveryDateType: Yup.string().required('Field is required'),
    autoAcceptPriceInPence: Yup.string().when('type', {
      is: (t) => t?.value === AuctionStatusType.PHYSICAL,
      then: () =>
        Yup.string()
          .required('Field is required')
          .max(10, 'Reserve Price must be between 1 and 10 characters')
          .test('minValue', 'Reserve Price must be greater than 0', (v) =>
            v ? Number(v.replaceAll('£', '').replaceAll(',', '')) > 0 : false,
          ),
    }),
    minimumPriceInPence: Yup.string().when('type', {
      is: (t) => t?.value === AuctionStatusType.MINIMUM_PRICE,
      then: () =>
        Yup.string()
          .required('Field is required')
          .max(10, 'Minimum Price must be between 1 and 10 characters'),
    }),
    endsOn: Yup.string()
      .nullable()
      .test('is-future-date', 'The date must be in the future', (value) => {
        if (!value) return true;
        return isBeforeOrPresentDate(value);
      }),
    description: Yup.string().required('Field is required'),
    v5cLogbook: Yup.object()
      .nullable()
      .test(
        'required-selection',
        'Field is required',
        (value) => value !== null && ['YES', 'NO'].includes(value?.value),
      ),

    ulezCompliant: Yup.object()
      .nullable()
      .test(
        'required-selection',
        'Field is required',
        (value) => value !== null && ['YES', 'NO'].includes(value?.value),
      ),

    vehicleType: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .nullable()
      .test(
        'valid-vehicle-type',
        `Possible values are: ${VehicleTypes.map((it) => it.value).join(', ')}`,
        (value) => {
          if (!value) return false;
          return VehicleTypes.map((it) => it.value).includes(value.value);
        },
      )
      .required('Field is required'),

    vatQualifying: Yup.object()
      .nullable()
      .test('required-selection', 'Field is required', (value) => value?.value !== null),
  });
